import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { BookOpen, ShieldCheck, User, ChevronRight, ImageIcon, Users } from "lucide-react";
import { PageHero, PageBody, Lightbox, slugify } from "./_shared";
import { useNursing } from "./NursingLayout";
import PhotoCarousel from "@/components/PhotoCarousel";

const ACCENTS = [
  { bg: "from-[#0F2A5F] to-[#1B3E83]", chip: "bg-[#E0F5EE] text-[#0E916F]" },
  { bg: "from-[#0FB28A] to-[#0E916F]", chip: "bg-[#E0F5EE] text-[#0E916F]" },
  { bg: "from-[#0FB28A] to-[#0E916F]", chip: "bg-[#E0F5EE] text-[#0E916F]" },
  { bg: "from-[#1B3E83] to-[#0A1F47]", chip: "bg-[#E0F5EE] text-[#0E916F]" },
];

const isHodByDesignation = (s) => {
  const d = (s?.designation || "").toLowerCase();
  return /\b(hod|head\s+of\s+department)\b/i.test(d) || d.includes("head of department");
};

export default function Departments() {
  const data = useNursing();
  const list = useMemo(
    () => (data.departments || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data.departments]
  );
  const allStaff = data.staff || [];
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const queryDeptId = searchParams.get("d");
  const findBySlug = (s) => (s ? list.find((d) => slugify(d.name) === s.toLowerCase()) : null);
  const initialId =
    findBySlug(slug)?.id ||
    (queryDeptId && list.some((d) => d.id === queryDeptId) ? queryDeptId : list[0]?.id) ||
    null;
  const [activeId, setActiveId] = useState(initialId);
  const [lbIdx, setLbIdx] = useState(-1);

  useEffect(() => {
    const bySlug = findBySlug(slug);
    if (bySlug) {
      setActiveId(bySlug.id);
      setLbIdx(-1);
      return;
    }
    if (queryDeptId && list.some((d) => d.id === queryDeptId)) {
      setActiveId(queryDeptId);
      setLbIdx(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, queryDeptId, list]);

  const active = list.find((d) => d.id === activeId) || null;
  const activeIdx = list.findIndex((d) => d.id === activeId);
  const accent = ACCENTS[Math.max(0, activeIdx) % ACCENTS.length];
  const photos = active?.photos || [];
  const deptStaff = allStaff
    .filter((s) => active && s.department_id === active.id)
    .sort((a, b) => {
      const hodId = active?.head_of_department_id;
      if (hodId) {
        if (a.id === hodId) return -1;
        if (b.id === hodId) return 1;
      }
      const aH = isHodByDesignation(a);
      const bH = isHodByDesignation(b);
      if (aH && !bH) return -1;
      if (!aH && bH) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });

  return (
    <div data-testid="nur-departments">
      <PageHero eyebrow="Academics" title="Departments &amp; Programmes" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-2xl ring-1 ring-[#E2E8F0] p-12 text-[#475569]">
            No active departments are published right now. Please check back soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            <aside className="bg-white rounded-2xl ring-1 ring-[#E2E8F0] overflow-hidden self-start shadow-sm" data-testid="nur-departments-list">
              <div className="px-4 py-3 border-b border-[#E2E8F0]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold">Browse</div>
                <div className="font-['Poppins',sans-serif] font-bold text-lg text-[#0F2A5F]">All Departments ({list.length})</div>
              </div>
              <ul>
                {list.map((d, i) => {
                  const isActive = d.id === active?.id;
                  const acc = ACCENTS[i % ACCENTS.length];
                  return (
                    <li key={d.id}>
                      <button type="button" onClick={() => { setActiveId(d.id); setLbIdx(-1); }}
                        data-testid={`nur-dept-tab-${d.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive ? "bg-[#E0F5EE] border-[#0FB28A]" : "border-transparent hover:bg-[#F4F6FA]"
                        }`}>
                        <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${acc.bg} text-white flex items-center justify-center shrink-0 shadow-sm`}>
                          <BookOpen size={14} strokeWidth={1.5} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm leading-snug ${isActive ? "text-[#0F2A5F] font-semibold" : "text-[#475569]"}`}>{d.name}</span>
                          {d.code && <span className="block text-[10px] text-[#475569] font-mono uppercase tracking-wider mt-0.5">{d.code}</span>}
                        </span>
                        <ChevronRight size={14} strokeWidth={1.5} className={isActive ? "text-[#0FB28A]" : "text-[#CBD5E1]"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {active && (
              <article key={active.id} data-testid={`nur-dept-${active.id}`}
                className="bg-white rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] shadow-sm">
                <div className={`bg-gradient-to-br ${accent.bg} text-white p-7 relative overflow-hidden`}>
                  <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white mb-3 shadow-md">
                      <BookOpen size={20} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-['Poppins',sans-serif] font-bold text-2xl sm:text-3xl leading-tight">{active.name}</h2>
                    {active.code && (
                      <div className="mt-1 text-xs uppercase tracking-wider opacity-80 font-mono">{active.code}</div>
                    )}
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {active.course_type && (
                      <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded ${accent.chip} font-bold`}>
                        {active.course_type}
                      </span>
                    )}
                    {active.approval_body && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded bg-[#E0F5EE] text-[#0E916F] font-bold">
                        <ShieldCheck size={10} strokeWidth={2} />
                        {active.approval_body}
                      </span>
                    )}
                  </div>
                  {active.head_of_department && (
                    <div className="flex items-center gap-2 text-sm text-[#0F2A5F] mb-4">
                      <User size={14} strokeWidth={1.5} className="text-[#0FB28A]" />
                      <span><span className="text-[#475569]">HoD:</span> <span className="font-semibold">{active.head_of_department}</span></span>
                    </div>
                  )}

                  <div className={`grid gap-6 ${photos.length > 0 ? "lg:grid-cols-[1fr_auto]" : "grid-cols-1"}`}>
                    <div className="min-w-0">
                      {active.about_html ? (
                        <div className="prose prose-sm prose-slate max-w-none text-[#0F2A5F]"
                             dangerouslySetInnerHTML={{ __html: active.about_html }} />
                      ) : (
                        <div className="text-sm text-[#475569] italic">Department details coming soon.</div>
                      )}
                    </div>
                    {photos.length > 0 && (
                      <div className="lg:w-[380px] shrink-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold mb-2 flex items-center gap-1.5">
                          <ImageIcon size={11} strokeWidth={1.5} /> Glimpses ({photos.length})
                        </div>
                        <PhotoCarousel
                          photos={photos}
                          onPhotoClick={(idx) => setLbIdx(idx)}
                          name={active.name}
                          testidPrefix="nur-dept-photo"
                          accentClass="bg-emerald-500"
                          className="w-full aspect-square rounded-xl overflow-hidden ring-1 ring-[#E2E8F0]"
                        />
                      </div>
                    )}
                  </div>

                  {deptStaff.length > 0 && (
                    <div className="mt-8">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold mb-3 flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.5} /> Faculty &amp; Staff ({deptStaff.length})
                      </div>
                      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {deptStaff.map((s) => {
                          const isHOD = active?.head_of_department_id === s.id || isHodByDesignation(s);
                          return (
                            <li key={s.id} data-testid={`nur-dept-staff-${s.id}`}
                              className={`rounded-xl p-3 flex items-center gap-3 ${
                                isHOD ? "ring-2 ring-[#0FB28A] bg-[#E0F5EE]" : "ring-1 ring-[#E2E8F0] bg-[#F4F6FA]"
                              }`}>
                              {s.photo_url ? (
                                <img src={s.photo_url} alt={s.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0" loading="lazy" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-[#E0F5EE] flex items-center justify-center text-[#0E916F] shrink-0">
                                  <User size={18} strokeWidth={1.5} />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-[#0F2A5F] truncate flex items-center gap-1.5">
                                  {s.name}
                                  {isHOD && (
                                    <span data-testid={`nur-dept-hod-badge-${s.id}`}
                                      className="shrink-0 inline-block text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded bg-[#0FB28A] text-white">
                                      HOD
                                    </span>
                                  )}
                                </div>
                                {s.designation && (<div className="text-[11px] text-[#475569] truncate">{s.designation}</div>)}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
      {active && lbIdx >= 0 && photos.length > 0 && (
        <Lightbox photos={photos.map((p) => ({ image_url: p.url, caption: active.name }))}
                  idx={lbIdx} onIdx={setLbIdx} onClose={() => setLbIdx(-1)} />
      )}
    </div>
  );
}
